import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { useAuth } from "../../context/AuthContext";
import { markPageTipSeen } from "../../services/pipService";
import { usePipAnchorRect } from "./PipContext";
import PipCharacter from "./PipCharacter";

function tipStyle(rect) {
  if (!rect) return null;
  const gap = 10;
  const estimatedWidth = Math.min(280, window.innerWidth - 24);
  const spaceBelow = window.innerHeight - (rect.top + rect.height);
  const placeAbove = spaceBelow < 88 && rect.top > 88;

  let left = rect.left + rect.width / 2;
  const half = estimatedWidth / 2;
  left = Math.min(Math.max(12 + half, left), window.innerWidth - 12 - half);

  if (placeAbove) {
    return {
      top: Math.max(12, rect.top - gap),
      left,
      transform: "translate(-50%, -100%)",
    };
  }

  return {
    top: rect.top + rect.height + gap,
    left,
    transform: "translateX(-50%)",
  };
}

export default function PipPageTip({
  anchorId,
  flag,
  text,
}) {
  const { currentUser, userPerks, refreshUserPerks } = useAuth();
  const rect = usePipAnchorRect(anchorId);
  const [ready, setReady] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const shouldShow = Boolean(
    currentUser &&
    userPerks?.fanOnboardingComplete &&
    !userPerks?.[flag]
  );

  useEffect(() => {
    if (!shouldShow) {
      setReady(false);
      return undefined;
    }
    const t = setTimeout(() => setReady(true), 500);
    return () => clearTimeout(t);
  }, [shouldShow, anchorId]);

  if (!shouldShow || !ready || dismissing) return null;

  const style = tipStyle(rect);
  if (!style) return null;

  const dismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      await markPageTipSeen(flag);
      if (refreshUserPerks && currentUser) await refreshUserPerks(currentUser.uid);
    } catch (err) {
      console.warn("Page tip dismiss failed:", err);
      setDismissing(false);
    }
  };

  return createPortal(
    <div
      className="pip-page-tip"
      style={style}
      role="status"
    >
      <PipCharacter size={42} state="peek" peek="B" />
      <p className="pip-page-tip-text">{text}</p>
      <button
        type="button"
        className="pip-page-tip-dismiss"
        onClick={dismiss}
        aria-label="Dismiss tip"
      >
        ×
      </button>
    </div>,
    document.body
  );
}
